import { PROJECT_STATUS_VALUES, PUNCH_STATUS_VALUES, type IconName, type ProjectStatus, type PunchStatus, type StageStatus } from "@sagot/constants";
import type { ChipTone } from "@sagot/ui";

export const PROJECT_PAGE_SIZE = 100;
export const PROJECT_SEARCH_DEBOUNCE_MS = 300;
export const PROJECT_NAME_LIMIT = 200;
export const PROJECT_TEXT_LIMIT = 2_000;
export const PUNCH_TITLE_LIMIT = 200;
export const PUNCH_TEXT_LIMIT = 2_000;
export const PUNCH_LOCATION_LIMIT = 200;
export const PUNCH_NOTE_LIMIT = 1_000;
export const PUNCH_REASON_LIMIT = 500;
export const PUNCH_REASON_MIN = 4;
export const PHOTO_CAPTION_LIMIT = 200;
export const DOCUMENT_TITLE_LIMIT = 160;
export const WARRANTY_MONTHS_MAX = 240;
/** The reason a lead gives when verifying an item on site: offered as the default text, still required (mirror of backend `VERIFIED_ON_SITE_REASON`). */
export const VERIFIED_ON_SITE_REASON = "verified on site";

/** Backend error codes this feature maps to messages (`app/core/errors.py`). */
export const STAGE_BLOCKED_CODE = "stage_blocked";
export const PROJECT_NOT_READY_CODE = "project_not_ready";
export const PROJECT_CLOSED_CODE = "project_closed";

/** The board's columns, left to right: where a project is in its life (the summary has no "current stage"). `paused` holds on-hold and cancelled ones. */
export const BOARD_COLUMNS = ["planned", "in_progress", "punch_list", "ready_for_acceptance", "handed_over", "paused"] as const;
export type BoardColumn = (typeof BOARD_COLUMNS)[number];

export function columnOf(status: ProjectStatus): BoardColumn {
  return status === "on_hold" || status === "cancelled" ? "paused" : status;
}

export const STATUS_OPTIONS: readonly ProjectStatus[] = PROJECT_STATUS_VALUES;

export const PROJECT_CHIPS = ["all", "active", "handed_over"] as const;
export type ProjectChip = (typeof PROJECT_CHIPS)[number];
export const PROJECT_VIEWS = ["board", "list"] as const;
export type ProjectView = (typeof PROJECT_VIEWS)[number];
export const PROJECT_PARAM = { view: "view", chip: "status", organisation: "org", lead: "lead" } as const;

export function isProjectView(value: string | null | undefined): value is ProjectView {
  return PROJECT_VIEWS.some((view) => view === value);
}
export function isProjectChip(value: string | null | undefined): value is ProjectChip {
  return PROJECT_CHIPS.some((chip) => chip === value);
}

export const PROJECT_STATUS_CHIP: Readonly<Record<ProjectStatus, { tone: ChipTone; icon: IconName }>> = {
  planned: { tone: "muted", icon: "calendar" },
  in_progress: { tone: "info", icon: "hard-hat" },
  punch_list: { tone: "warning", icon: "list-checks" },
  ready_for_acceptance: { tone: "accent", icon: "clipboard-check" },
  handed_over: { tone: "success", icon: "badge-check" },
  on_hold: { tone: "muted", icon: "pause-circle" },
  cancelled: { tone: "muted", icon: "x-circle" },
};

export const STAGE_LOOK: Readonly<Record<StageStatus, { tone: ChipTone; icon: IconName }>> = {
  pending: { tone: "muted", icon: "circle" },
  in_progress: { tone: "info", icon: "loader" },
  passed: { tone: "success", icon: "check-circle" },
  failed: { tone: "danger", icon: "x-circle" },
};

export const PUNCH_STATUS_CHIP: Readonly<Record<PunchStatus, { tone: ChipTone; icon: IconName }>> = {
  open: { tone: "warning", icon: "circle-dot" },
  in_progress: { tone: "info", icon: "play" },
  fixed: { tone: "accent", icon: "hourglass" },
  verified: { tone: "success", icon: "check-circle" },
  reopened: { tone: "danger", icon: "rotate-ccw" },
  waived: { tone: "muted", icon: "minus" },
};

export const PUNCH_FILTERS = ["all", ...PUNCH_STATUS_VALUES] as const;
export type PunchFilter = (typeof PUNCH_FILTERS)[number];
export const PUNCH_SORT_KEYS = ["number", "title", "status", "assignee"] as const;
export type PunchSortKey = (typeof PUNCH_SORT_KEYS)[number];

/** Project documents (stage reports, drawings, manuals...): the kinds the server knows (mirror of backend `ProjectDocumentKind`). */
export const PROJECT_DOCUMENT_KINDS = ["report", "drawing", "manual", "certificate", "photo", "other"] as const;
export type ProjectDocumentKind = (typeof PROJECT_DOCUMENT_KINDS)[number];

export const INSTALLATION_TAB_PARAM = "item";
